import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
	CircleCheck,
	CircleX,
	OctagonAlert,
	TriangleAlert,
	Info,
	Eye,
	Search,
	ShieldCheck,
	FileText,
	FileSearch,
	Layers,
	ClipboardList,
	ArrowUpRight,
	Share2,
	Download,
	ChevronDown,
	AlertCircle,
	RotateCcw,
	Lock
} from "lucide-react";
import { message } from "antd";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import PlanReviewFindingModal from "components/plan-review-finding-modal/plan-review-finding-modal";
import PlanReviewPageHeader, { PlanReviewHeaderTone } from "components/plan-review-page-header/plan-review-page-header";
import OllieAvatar, { ollieMoodForOutcome } from "components/ollie-avatar/ollie-avatar";
import PlanReviewLoadError from "components/plan-review-load-error/plan-review-load-error";
import PlanReviewApprovalAcknowledgmentModal, {
	isAhjSubmissionAcknowledgmentRequired
} from "components/plan-review-approval-acknowledgment-modal/plan-review-approval-acknowledgment-modal";
import {
	PlanReviewFindingHistoryCount,
	PlanReviewFindingReviewMarks
} from "components/plan-review-finding-review-marks/plan-review-finding-review-marks";
import PlanReviewMarkedIncorrectGroup from "components/plan-review-marked-incorrect-group/plan-review-marked-incorrect-group";

import { useGetPlanReviewJobSubscription } from "graphql/subscriptions/plan-review-job-by-id.generated";
import { useGetPlanReviewFindingsQuery } from "graphql/queries/get-plan-review-findings.generated";
import { useCreatePlanReviewCheckoutMutation } from "graphql/mutations/create-plan-review-checkout.generated";
import usePagePdfExport from "hooks/usePagePdfExport";
import { usePlanReviewFlowStore } from "state/zustand-stores/plan-review-flow-store";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { buildReportFileName } from "utils/page-pdf";
import { getPropertyAddress } from "utils/helpers";
import routeNames from "constants/route-names";
import { PLAN_REVIEW_ANALYZING_MESSAGES, PLAN_REVIEW_ANALYZING_MESSAGE_INTERVAL_MS } from "constants/constants";
import {
	PlanReviewJobStatus,
	PlanReviewFindingStatus,
	PlanReviewReviewOutcome
} from "constants/field-values";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildPlanReviewJobDescription } from "utils/ollie-screen-description";
import {
	buildFindingHistory,
	isFindingEditedByReviewer,
	isMarkedIncorrectByApplicant
} from "utils/plan-review-finding-review";

// `tone` tints the status text on the summary card, where the outcome has no pill.
const OUTCOME_META: Record<string, { label: string; tone: string }> = {
	[PlanReviewReviewOutcome.APPROVED]: { label: "Approved", tone: "prd-tone-approved" },
	[PlanReviewReviewOutcome.REVISIONS_NEEDED]: { label: "Revisions Needed", tone: "prd-tone-revisions" }
};

type Job = NonNullable<
	NonNullable<ReturnType<typeof useGetPlanReviewJobSubscription>["data"]>["plan_review_jobs_by_pk"]
>;
// Findings are not embedded in the job subscription any more: get_plan_review_findings
// (MWA-788) is what applies the paywall — the free tier is two counts and nothing else,
// so unpaid this resolves to an empty findings array, never a partial or redacted row.
type FindingsResult = NonNullable<ReturnType<typeof useGetPlanReviewFindingsQuery>["data"]>["get_plan_review_findings"];
type Finding = FindingsResult["findings"][number];

const DOC_TYPE_LABELS: Record<string, string> = {
	plans: "Plans",
	calculations: "Calculations",
	cut_sheets: "Cut Sheets",
	application_form: "Application Form",
	cover_letter: "Cover Letter",
	other: "Document"
};

// Category rollup status, in worst-first precedence. A FAIL check has priority over missing content.
type CategoryStatus = "Missing Content" | "Revisions Needed" | "Compliant";

const categoryStatus = (findings: Finding[]): CategoryStatus => {
	if (findings.some(f => f.status === PlanReviewFindingStatus.FAIL)) return "Revisions Needed";
	if (findings.some(f => f.status === PlanReviewFindingStatus.MISSING)) return "Missing Content";
	if (findings.some(f => f.status === PlanReviewFindingStatus.WARNING)) return "Revisions Needed";
	return "Compliant";
};

const CATEGORY_STATUS_META: Record<CategoryStatus, { className: string; icon: JSX.Element }> = {
	Compliant: { className: "prd-cat-compliant", icon: <CircleCheck size={20} /> },
	"Revisions Needed": { className: "prd-cat-revisions", icon: <TriangleAlert size={20} /> },
	"Missing Content": { className: "prd-cat-missing", icon: <OctagonAlert size={20} /> }
};

const formatBytes = (bytes?: number | null): string => {
	if (!bytes) return "";
	const units = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const checkPrefix = (status: string): string => {
	switch (status) {
		case PlanReviewFindingStatus.FAIL:
			return "FAIL: ";
		case PlanReviewFindingStatus.MISSING:
			return "MISSING: ";
		case PlanReviewFindingStatus.WARNING:
			return "WARNING: ";
		case PlanReviewFindingStatus.NOT_APPLICABLE:
			return "NOT APPLICABLE: ";
		default:
			return "";
	}
};

const checkIcon = (status: string, size = 16) => {
	switch (status) {
		case PlanReviewFindingStatus.PASS:
			return <CircleCheck size={size} className="prd-check-pass" />;
		case PlanReviewFindingStatus.WARNING:
			return <TriangleAlert size={size} className="prd-check-warn" />;
		case PlanReviewFindingStatus.FAIL:
			return <CircleX size={size} className="prd-check-fail" />;
		case PlanReviewFindingStatus.MISSING:
			return <OctagonAlert size={size} className="prd-check-fail" />;
		default:
			return <OctagonAlert size={size} className="prd-check-skip" />;
	}
};

// The checks of an opened category, in the order the reader should deal with them.
// Anything that is neither flagged nor passing (not applicable) falls into the last group.
const FINDING_GROUPS: { title: string; statuses: string[] | null }[] = [
	{
		title: "Attention required",
		statuses: [PlanReviewFindingStatus.FAIL, PlanReviewFindingStatus.WARNING, PlanReviewFindingStatus.MISSING]
	},
	{ title: "Compliant", statuses: [PlanReviewFindingStatus.PASS] },
	{ title: "Other", statuses: null }
];

const findingsInGroup = (findings: Finding[], statuses: string[] | null): Finding[] => {
	if (statuses) return findings.filter(f => statuses.includes(f.status ?? ""));
	const groupedStatuses = FINDING_GROUPS.flatMap(group => group.statuses ?? []);
	return findings.filter(f => !groupedStatuses.includes(f.status ?? ""));
};

// The compliance verdict, as one indicator: the report already says PASS/FAIL in its
// own vocabulary, so the card shows what it means for the submittal instead.
const COMPLIANCE_STATUS_META: Record<string, { label: string; className: string; icon: typeof CircleCheck }> = {
	PASS: { label: "Compliant", className: "prd-comp-pill-pass", icon: CircleCheck },
	WARNING: { label: "Revisions Needed", className: "prd-comp-pill-warning", icon: TriangleAlert },
	FAIL: { label: "Revisions Needed", className: "prd-comp-pill-fail", icon: CircleX },
	MISSING: { label: "Missing Content", className: "prd-comp-pill-fail", icon: OctagonAlert }
};

// The three flagged statuses as the comments list counts them.
const COMMENT_COUNT_META: [string, string, string][] = [
	[PlanReviewFindingStatus.FAIL, "Revisions Needed", "prd-comment-chip-fail"],
	[PlanReviewFindingStatus.MISSING, "Missing", "prd-comment-chip-fail"],
	[PlanReviewFindingStatus.WARNING, "Warning", "prd-comment-chip-warning"]
];

// Left accent of a finding row: indigo when the reviewer changed the finding, amber when it has
// reviewer changes in its history but its values are Ollie's again. `modifierPrefix` is the class of the row, for example "prd-check-row".
const findingRowAccentClass = (finding: Finding, historyLength: number, modifierPrefix: string): string => {
	if (isFindingEditedByReviewer(finding)) return ` ${modifierPrefix}-edited`;
	if (historyLength > 1) return ` ${modifierPrefix}-history`;
	return "";
};

/**
 * A finding row of the report, with the review marks under its text and the history count on the
 * right. `rowClassName` has the classes of the row; `modifierPrefix` builds its accent classes.
 */
const FindingRow = ({
	finding,
	rowClassName,
	modifierPrefix,
	label,
	icon,
	onSelectFinding
}: {
	finding: Finding;
	rowClassName: string;
	modifierPrefix: string;
	label: string;
	icon: JSX.Element;
	onSelectFinding: (findingId: string) => void;
}) => {
	const historyLength = buildFindingHistory(finding).length;
	const accentClassName = findingRowAccentClass(finding, historyLength, modifierPrefix);
	const mutedClassName = isMarkedIncorrectByApplicant(finding) ? ` ${modifierPrefix}-muted` : "";

	return (
		<button
			type="button"
			className={`${rowClassName}${accentClassName}${mutedClassName}`}
			onClick={() => onSelectFinding(finding.id)}
		>
			{icon}
			<span className="prd-finding-row-content">
				<span>{label}</span>
				<PlanReviewFindingReviewMarks finding={finding} />
			</span>
			{historyLength > 1 && <PlanReviewFindingHistoryCount count={historyLength} />}
		</button>
	);
};

const ReviewCategory = ({
	name,
	findings,
	forceExpanded,
	showMarkedIncorrectInPlace,
	onSelectFinding
}: {
	name: string;
	findings: Finding[];
	/** Opens the accordion while the page is being captured for the PDF. */
	forceExpanded: boolean;
	/** Findings marked Incorrect by the applicant are listed in their own group unless this is on. */
	showMarkedIncorrectInPlace: boolean;
	onSelectFinding: (findingId: string) => void;
}) => {
	const [open, setOpen] = useState(false);
	const isOpen = open || forceExpanded;
	const status = categoryStatus(findings);
	const meta = CATEGORY_STATUS_META[status];
	const isEmptyMissing = findings.length === 0;
	const listedFindings = showMarkedIncorrectInPlace
		? findings
		: findings.filter(finding => !isMarkedIncorrectByApplicant(finding));

	return (
		<div className="prd-category">
			<button className="prd-category-head" onClick={() => setOpen(o => !o)}>
				<span className={`prd-category-badge-icon ${meta.className}`}>{meta.icon}</span>
				<span className="prd-category-heading">
					<span className="prd-category-title">{name}</span>
					<span className="prd-category-meta">
						<span className={`prd-category-status ${meta.className}`}>{status}</span>
						{findings.length > 0 && (
							<span className="prd-category-count">
								{findings.length} {findings.length === 1 ? "check" : "checks"} completed
							</span>
						)}
					</span>
				</span>
				<ChevronDown size={20} className={`prd-category-chevron ${isOpen ? "prd-open" : ""}`} />
			</button>

			{isOpen && (
				<div className="prd-category-body">
					{isEmptyMissing && (
						<div className="prd-category-empty">
							<Info size={16} />
							<div>
								<p className="prd-empty-title">No files submitted for this category</p>
								<p className="prd-empty-text">Please upload the required documents.</p>
							</div>
						</div>
					)}

					{FINDING_GROUPS.map(group => {
						const groupFindings = findingsInGroup(listedFindings, group.statuses);
						if (groupFindings.length === 0) return null;
						return (
							<div key={group.title} className="prd-checks">
								<div className="prd-checks-head">
									<span className="prd-checks-title">{group.title}</span>
									<span className="prd-checks-hint">
										<Search size={12} />
										Click items to view details
									</span>
								</div>
								{groupFindings.map(f => (
									<FindingRow
										key={f.id}
										finding={f}
										rowClassName="prd-check-row prd-check-row-clickable"
										modifierPrefix="prd-check-row"
										label={`${checkPrefix(f.status ?? "")}${f.title ?? ""}`}
										icon={<span className="prd-check-row-icon">{checkIcon(f.status ?? "", 20)}</span>}
										onSelectFinding={onSelectFinding}
									/>
								))}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

// Steps through the analyzing messages on a timer so a long review does not look frozen.
// It stops on the last message instead of starting over.
const AnalyzingMessage = () => {
	const [messageIndex, setMessageIndex] = useState(0);
	const lastMessageIndex = PLAN_REVIEW_ANALYZING_MESSAGES.length - 1;

	useEffect(() => {
		const timer = setInterval(
			() => setMessageIndex(current => Math.min(current + 1, lastMessageIndex)),
			PLAN_REVIEW_ANALYZING_MESSAGE_INTERVAL_MS
		);

		return () => clearInterval(timer);
	}, [lastMessageIndex]);

	return <>{PLAN_REVIEW_ANALYZING_MESSAGES[messageIndex]}</>;
};

const PlanReviewDetails = () => {
	const navigate = useNavigate();
	const params = useParams<{ planReviewId: string; jobId: string }>();
	const jobId = params.jobId as string;
	const planReviewId = params.planReviewId as string;
	const { loggedUserValue } = useLoggedUser();
	// Only the id is kept in state; the row is re-derived from the live subscription on
	// every render, so the modal never shows a stale copy.
	const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
	// The comments list is collapsed on arrival: the report is read top to bottom, and
	// the same items are laid out in full on the comments screen.
	const [commentsExpanded, setCommentsExpanded] = useState(false);
	// Findings marked Incorrect by the applicant are listed in their own group at the end, unless
	// the reader asks to see them in their normal place too.
	const [showMarkedIncorrectInPlace, setShowMarkedIncorrectInPlace] = useState(false);
	const { exportRef, isExporting, exportToPdf } = usePagePdfExport<HTMLDivElement>();

	const { data, loading, error } = useGetPlanReviewJobSubscription({
		skip: !jobId,
		variables: { id: jobId }
	});

	const job = data?.plan_review_jobs_by_pk;

	const {
		data: findingsData,
		loading: loadingFindings,
		refetch: refetchFindings
	} = useGetPlanReviewFindingsQuery({
		skip: !jobId,
		variables: { jobId },
		fetchPolicy: "cache-and-network"
	});
	const findings: Finding[] = useMemo(() => findingsData?.get_plan_review_findings.findings ?? [], [findingsData]);
	// The findings query has no subscription of its own — they only exist once, produced
	// by one analysis run — so this is what makes the report replace its empty findings
	// with the real ones the moment the live job subscription reports COMPLETE, without
	// the reader having to reload.
	const previousJobStateRef = useRef<string | undefined>(job?.state);
	useEffect(() => {
		if (job?.state === PlanReviewJobStatus.COMPLETE && previousJobStateRef.current !== PlanReviewJobStatus.COMPLETE) {
			refetchFindings();
		}
		previousJobStateRef.current = job?.state;
	}, [job?.state, refetchFindings]);
	// The whole paywall reads off these three: unpaid, findings is always empty and these
	// two counts are the only thing shown — never a per-finding title, category or status.
	const isLocked = findingsData?.get_plan_review_findings.is_locked ?? false;
	const passedCount = findingsData?.get_plan_review_findings.passed_count ?? 0;
	const needsAttentionCount = findingsData?.get_plan_review_findings.needs_attention_count ?? 0;

	const [createPlanReviewCheckout] = useCreatePlanReviewCheckoutMutation();
	const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);
	const handleUnlock = useCallback(async () => {
		if (!job?.plan_review_id) return;
		setIsRedirectingToCheckout(true);
		try {
			const result = await createPlanReviewCheckout({
				variables: {
					input: {
						plan_review_id: job.plan_review_id,
						cycle_number: null,
						success_url: window.location.href,
						cancel_url: window.location.href
					}
				}
			});
			const checkoutUrl = result.data?.create_plan_review_checkout?.checkout_url;
			if (!checkoutUrl) throw new Error("Could not start checkout.");
			window.location.href = checkoutUrl;
		} catch (checkoutError) {
			console.error("Create plan review checkout failed:", checkoutError);
			message.error("We couldn't start checkout. Please try again.");
			setIsRedirectingToCheckout(false);
		}
	}, [job?.plan_review_id, createPlanReviewCheckout]);

	// Tells Ollie which report is on screen, so "why did this fail?" has a subject.
	useDescribeScreenToOllie(
		buildPlanReviewJobDescription({
			jobId: job?.id,
			planReviewId: job?.plan_review_id,
			submissionTypeId: job?.plan_review?.submission_type?.id,
			submissionNumber: job?.submission_number,
			propertyName: job?.plan_review?.property?.name,
			jurisdictionName: job?.plan_review?.jurisdiction_record?.name,
			submissionTypeName: job?.plan_review?.submission_type?.name,
			reviewType: job?.plan_review?.review_type,
			state: job?.state,
			reviewOutcome: job?.review_outcome,
			complianceScore: job?.compliance_score,
			documentCount: job?.plan_review_documents?.length,
			findingCount: findings.length,
			propertyId: job?.plan_review?.property_id,
			jurisdictionId: job?.plan_review?.jurisdiction_id,
			organizationId: job?.plan_review?.organization_id,
			workspaceId: job?.plan_review?.workspace_id,
			createdByUserId: job?.plan_review?.created_by_user_id
		}),
		{ propertyId: job?.plan_review?.property_id, jurisdictionId: job?.plan_review?.jurisdiction_id }
	);


	const setSelection = usePlanReviewFlowStore(state => state.setSelection);

	// Start a new submission for the same project. The upload screen starts empty: the
	// revision is only the documents the applicant uploads in answer to this report,
	// and this submittal keeps the package it was reviewed on. The project id travels
	// in the URL; the store is primed with the same context so the upload screen renders
	// without waiting for the fetch.
	const handleResubmit = useCallback(
		(withThisPackage = false) => {
			if (!job) return;
			const planReview = job.plan_review;
			setSelection({
				organizationId: planReview?.organization_id ?? "",
				propertyId: planReview?.property_id ?? "",
				jurisdictionId: planReview?.jurisdiction_id ?? "",
				submissionTypeId: planReview?.submission_type?.id ?? "",
				propertyName: planReview?.property?.name ?? "",
				// Only when the run failed: the package was never reviewed, so the
				// applicant fixes what broke it instead of uploading it all again.
				...(withThisPackage
					? {
							documents: (job.plan_review_documents ?? []).map(doc => ({
								localId: doc.id,
								name: doc.file_name,
								mimeType: doc.mime_type,
								size: doc.file_size_bytes ?? 0,
								publicUrl: doc.file_url,
								docType: doc.doc_type ?? "other"
							}))
						}
					: {})
			});
			navigate(routeNames.PLAN_REVIEW_COLLECTION_RESUBMIT.replace(":planReviewId", job.plan_review_id ?? planReviewId));
		},
		[job, planReviewId, setSelection, navigate]
	);

	const selectedFinding = useMemo(
		() => findings.find(f => f.id === selectedFindingId) ?? null,
		[findings, selectedFindingId]
	);

	// plan_review_findings carries only the document id and name; the modal also shows
	// the file itself, which lives on the job's document list.
	const selectedFindingDocument = useMemo(() => {
		if (!selectedFinding?.document_id) return null;
		return (job?.plan_review_documents ?? []).find(d => d.id === selectedFinding.document_id) ?? null;
	}, [job, selectedFinding]);

	const report = useMemo(() => {
		const rj = job?.report_json;
		if (!rj) return null;
		return typeof rj === "string" ? JSON.parse(rj) : rj;
	}, [job?.report_json]);

	const categories = useMemo(() => {
		const map = new Map<string, { findings: Finding[]; position: number }>();
		for (const f of findings) {
			// The category as it reads today while the office still has it, and as the
			// report recorded it once they no longer do.
			const key = f.category_record?.name ?? f.category ?? "Other";
			const group = map.get(key);
			if (group) group.findings.push(f);
			else map.set(key, { findings: [f], position: f.category_record?.sort_order ?? 999 });
		}
		// Render in the jurisdiction's order; a category it has dropped falls to the end.
		return [...map.entries()]
			.sort(([, a], [, b]) => a.position - b.position)
			.map(([name, group]) => [name, group.findings] as [string, Finding[]]);
	}, [findings]);

	// Every finding id in the order the Review Categories list lays them out, so
	// Previous/Next in the modal walks the report the reader is looking at.
	const navigableFindingIds = useMemo(
		() => categories.flatMap(([, findings]) => findings.map(finding => finding.id)),
		[categories]
	);

	const selectedFindingIndex = selectedFindingId ? navigableFindingIds.indexOf(selectedFindingId) : -1;
	const previousFindingId = selectedFindingIndex > 0 ? navigableFindingIds[selectedFindingIndex - 1] : null;
	const nextFindingId =
		selectedFindingIndex >= 0 && selectedFindingIndex < navigableFindingIds.length - 1
			? navigableFindingIds[selectedFindingIndex + 1]
			: null;

	// The findings the applicant has to act on. Passing and skipped checks are part of
	// the report but nobody counts them as findings.
	const flaggedFindings = useMemo(
		() =>
			findings.filter(
				f =>
					f.status === PlanReviewFindingStatus.FAIL ||
					f.status === PlanReviewFindingStatus.MISSING ||
					f.status === PlanReviewFindingStatus.WARNING
			),
		[findings]
	);

	// needsAttentionCount, not flaggedFindings.length: it has to read right even while
	// locked, when findings is empty and flaggedFindings can never reflect the real count.
	const hasIssues = needsAttentionCount > 0;

	const editedFindingsCount = findings.filter(isFindingEditedByReviewer).length;
	const markedIncorrectFindings = findings.filter(isMarkedIncorrectByApplicant);
	const listedFlaggedFindings = showMarkedIncorrectInPlace
		? flaggedFindings
		: flaggedFindings.filter(finding => !isMarkedIncorrectByApplicant(finding));
	const isLatestSubmittal = job?.plan_review?.latest_submittal?.[0]?.id === job?.id;

	// Its own state because it takes minutes, and showing "Analyzing" through it would
	// tell the user something that is not happening yet.
	const isPreparing = job?.state === PlanReviewJobStatus.PREPARING;
	const isAnalyzing = job?.state === PlanReviewJobStatus.ANALYZING || job?.state === PlanReviewJobStatus.COLLECTING;
	const isFailed = job?.state === PlanReviewJobStatus.FAILED;

	// Which attempt of the project this report belongs to. The first one says nothing new.
	const submissionNumber = Number(job?.submission_number ?? 1);
	const isResubmission = submissionNumber > 1;

	// ── Nothing to draw ──────────────────────────────────────────────────────
	// The whole report reads off the submittal, so without it the page renders as an
	// empty shell — scores at zero, no findings, no documents — which says the review
	// came back clean rather than that it never arrived.
	if (!loading && !job) return <PlanReviewLoadError subject="this submittal" notFound={!error} />;

	// ── Preparing / analyzing state ──────────────────────────────────────────
	if (isPreparing || isAnalyzing) {
		return (
			<OllieLayout>
				<div className="psf-page">
					<div className="prd-analyzing">
						<div className="prd-analyzing-orb">
							<div className="prd-analyzing-aura" />
							<div className="prd-analyzing-ring-outer" />
							<div className="prd-analyzing-ring-inner" />
							<div className="prd-analyzing-avatar">
								<img src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-analyzing.svg`} alt="Ollie AI" />
							</div>
						</div>
						<h2 className="prd-analyzing-title">
							{isPreparing
								? "Preparing Your Documents"
								: isResubmission
									? "Re-analyzing Submittal Package"
									: "Ollie is reviewing your project plans…"}
						</h2>
						{isResubmission && <span className="prd-submission-pill">Submission #{submissionNumber}</span>}
						<p className="prd-analyzing-text">
							{isPreparing
								? "Optimizing large plan sheets for review — this can take a few minutes on big packages…"
								: <AnalyzingMessage />}
						</p>
					</div>
				</div>
			</OllieLayout>
		);
	}

	// ── Failed state ─────────────────────────────────────────────────────────
	if (isFailed) {
		return (
			<OllieLayout>
				<div className="psf-page">
					<div className="prd-interstitial">
						<div className="prd-interstitial-icon prd-interstitial-icon-fail">
							<AlertCircle size={34} />
						</div>
						<h2 className="prd-interstitial-title">The review couldn&apos;t be completed</h2>
						{isResubmission && <span className="prd-submission-pill">Submission #{submissionNumber}</span>}
						<p className="prd-interstitial-text">{job?.error_message || "Something went wrong during the analysis."}</p>
						<button className="prd-view-report-btn prd-retry" onClick={() => handleResubmit(true)}>
							<RotateCcw size={16} />
							Review the package
						</button>
					</div>
				</div>
			</OllieLayout>
		);
	}

	// ── Full compliance report ───────────────────────────────────────────────
	const compliance = report?.overall_compliance;
	const observation = report?.overall_observation as string | undefined;
	const propertyAddress = getPropertyAddress(
		job?.plan_review?.property?.address,
		job?.plan_review?.property?.standardized_address
	);
	const submittedDate = job?.created_at ? String(job.created_at).slice(0, 10) : "";
	const documents = job?.plan_review_documents ?? [];

	// Overall readiness badge: prefer the backend-computed review_outcome, falling
	// back to a derivation from the findings for jobs analyzed before it existed.
	const outcome =
		job?.review_outcome ??
		report?.review_outcome ??
		(hasIssues ? PlanReviewReviewOutcome.REVISIONS_NEEDED : PlanReviewReviewOutcome.APPROVED);
	const outcomeMeta = OUTCOME_META[outcome] ?? OUTCOME_META[PlanReviewReviewOutcome.REVISIONS_NEEDED];
	const headerTone: PlanReviewHeaderTone = outcome === PlanReviewReviewOutcome.APPROVED ? "approved" : "revisions";

	const complianceStatus = (compliance?.status ?? "FAIL") as string;
	const complianceMeta = COMPLIANCE_STATUS_META[complianceStatus] ?? COMPLIANCE_STATUS_META.FAIL;
	const ComplianceIcon = complianceMeta.icon;

	// The PDF is a capture of the report as it renders here, so the only thing to build is
	// the file name: what tells two exports apart in a folder.
	const propertyName = job?.plan_review?.property?.name || "Plan Review";
	const handleExport = () =>
		exportToPdf(buildReportFileName(propertyName, `Plan Review ${planReviewId}`, `Submission ${jobId}`));

	const mustAcknowledgeAhjSubmission = isAhjSubmissionAcknowledgmentRequired({
		reviewOutcome: job?.review_outcome,
		userAcknowledgments: job?.ahj_submission_acknowledgments,
		userRole: loggedUserValue.role
	});

	return (
		<>
			<OllieLayout>
				<div className={`prd-report-shell${isExporting ? " prd-report-shell-exporting" : ""}`} ref={exportRef}>
					<PlanReviewPageHeader
						propertyName={job?.plan_review?.property?.name}
						address={propertyAddress}
						submittedLabel={submittedDate ? `Submitted ${dayjs(job?.created_at).format("MMMM D, YYYY")}` : null}
						status={{ label: outcomeMeta.label, tone: headerTone }}
						statusNote={editedFindingsCount > 0 ? `Includes reviewer edits (${editedFindingsCount})` : null}
						submissionType={job?.plan_review?.submission_type}
						ahjName={job?.plan_review?.jurisdiction_record?.name}
						ollieId={job?.plan_review?.ollie_id}
					>
						{/* Sharing a report outside the workspace is its own ticket; the button is
						    built here so the header lands in one piece, and stays hidden until then. */}
						<button className="prph-btn-secondary" onClick={() => undefined} hidden>
							<Share2 size={16} />
							Share
						</button>
						<button className="prph-btn-secondary" onClick={handleExport} disabled={isExporting}>
							<Download size={16} />
							{isExporting ? "Exporting…" : "Export"}
						</button>
						<button
							className="prph-btn-primary"
							onClick={() => handleResubmit()}
							disabled={isLocked}
							title={isLocked ? "Pay the Ollie pre-screening fee to submit revisions." : undefined}
						>
							<RotateCcw size={16} />
							Submit Revisions
						</button>
					</PlanReviewPageHeader>

					<div className="prd-report-page">
						<div className="prd-report">
							{/* Submitted documents */}
							<div className="prd-section">
								<h2 className="prd-section-title">
									<FileText size={18} className="prd-section-icon" />
									Submitted Documents
								</h2>
								<div className="prd-docs-grid">
									{documents.map(doc => (
										<a
											key={doc.id}
											href={doc.file_url}
											target="_blank"
											rel="noreferrer"
											className="prd-doc-card"
											title={doc.file_name}
										>
											<div className="prd-doc-icon">
												<FileText size={18} />
											</div>
											<div className="prd-doc-info">
												<span className="prd-doc-name">{doc.file_name}</span>
												<span className="prd-doc-meta">
													{DOC_TYPE_LABELS[doc.doc_type ?? "other"] ?? "Document"}
													{doc.file_size_bytes ? ` · ${formatBytes(doc.file_size_bytes)}` : ""}
												</span>
											</div>
										</a>
									))}
								</div>
							</div>

							{/* Submittal summary — the whole job summary, which the submissions list can only clip */}
							<div className="prd-section">
								<h2 className="prd-section-title prd-section-title-plain">Submittal Summary</h2>
								<div className={`prd-summary-card prd-summary-card-${headerTone}`}>
									{/* Ollie reacts to the verdict the card is reporting, so the reader gets it
								    before reading a word of the summary. */}
									<div className="prd-summary-avatar">
										<OllieAvatar mood={ollieMoodForOutcome(outcome)} />
									</div>

									<div className="prd-summary-main">
										<div className="prd-summary-head">
											<span className="prd-summary-icon">
												<FileText size={20} />
											</span>
											<h3 className="prd-summary-title">Submittal #{submissionNumber}</h3>
											{editedFindingsCount > 0 && (
												<span className="prd-summary-adjusted">
													{editedFindingsCount} {editedFindingsCount === 1 ? "finding" : "findings"} adjusted by reviewer
												</span>
											)}
										</div>
										<p className="prd-summary-text">{job?.summary || "No summary was produced for this submission."}</p>
									</div>

									<div className="prd-summary-side">
										<div className="prd-summary-facts">
											<div className="prd-summary-fact">
												<span className="prd-summary-label">Date</span>
												<span className="prd-summary-value">
													{job?.created_at ? dayjs(job.created_at).format("MMM D, YYYY") : "—"}
												</span>
											</div>
											<div className="prd-summary-fact">
												<span className="prd-summary-label">Status</span>
												<span className={`prd-summary-value prd-summary-outcome ${outcomeMeta.tone}`}>
													<i className="prd-summary-dot" />
													{outcomeMeta.label}
												</span>
											</div>
											<div className="prd-summary-fact">
												<span className="prd-summary-label">Docs Reviewed</span>
												<span className="prd-summary-metric">{documents.length}</span>
											</div>
											<div className="prd-summary-fact">
												<span className="prd-summary-label">Passed</span>
												<span className="prd-summary-metric">{passedCount}</span>
											</div>
											<div className="prd-summary-fact">
												<span className="prd-summary-label">Needs Attention</span>
												<span className={`prd-summary-metric ${hasIssues ? "prd-tone-revisions" : ""}`}>
													{needsAttentionCount}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Ollie plan review paywall (MWA-788): the summary and the two counts above
							    are the whole free tier. Nothing below this point — no title, no category,
							    no per-finding anything — renders until the project's initial fee is paid;
							    get_plan_review_findings returns an empty findings array until then, so
							    there is nothing to blur, only something to not render. */}
							{isLocked && (
								<div className="prd-paywall">
									<Lock size={18} className="prd-paywall-icon" />
									<div className="prd-paywall-copy">
										<p className="prd-paywall-title">Unlock the full report</p>
										<p className="prd-paywall-text">
											Pay the Ollie pre-screening fee to see what to fix and where for each item, and to unlock
											resubmittals for this project.
										</p>
									</div>
									<button className="prph-btn-primary" onClick={handleUnlock} disabled={isRedirectingToCheckout}>
										{isRedirectingToCheckout ? "Redirecting…" : "Unlock full report"}
									</button>
								</div>
							)}

							{/* Analysis block */}
							{!isLocked && (
							<div className="prd-analysis">
								<div className="prd-analysis-head">
									<div className="prd-analysis-mark">
										<FileSearch size={20} />
									</div>
									<h2 className="prd-analysis-title">Ollie&apos;s Analysis &amp; Recommended Actions</h2>
								</div>

								{/* Observations */}
								<div className="prd-analysis-card">
									<div className="prd-analysis-card-head">
										<div className="prd-analysis-card-heading">
											<Eye size={18} />
											<span>Observations</span>
										</div>
									</div>
									<p className="prd-analysis-body">{observation || job?.summary}</p>
								</div>

								{/* Compliance */}
								<div className="prd-analysis-card">
									<div className="prd-analysis-card-head">
										<div className="prd-analysis-card-heading">
											<ShieldCheck size={18} />
											<span>Compliance</span>
										</div>
										<span className={`prd-comp-pill ${complianceMeta.className}`}>
											<ComplianceIcon size={14} />
											{complianceMeta.label}
										</span>
									</div>

									<div className="prd-compliance-inner">
										{compliance?.summary && <p className="prd-compliance-summary">{compliance.summary}</p>}

										{/* An approved submittal needs no revisions, whatever the model wrote here. */}
										{outcome !== PlanReviewReviewOutcome.APPROVED &&
											compliance?.corrective_action &&
											compliance.corrective_action !== "null" && (
												<div className="prd-compliance-action">
													<AlertCircle size={18} className="prd-compliance-action-icon" />
													<div>
														<p className="prd-compliance-action-label">Recommended Action</p>
														<p className="prd-compliance-action-text">{compliance.corrective_action}</p>
													</div>
												</div>
											)}

										{/* Plan review comments — the same items the applicant is sent, listed
									    here so the reviewer never has to leave the report to see them. */}
										<div className="prd-comments">
											<div
												className="prd-comments-head"
												role="button"
												tabIndex={0}
												aria-expanded={commentsExpanded || isExporting}
												onClick={() => setCommentsExpanded(open => !open)}
												onKeyDown={event => {
													if (event.key !== "Enter" && event.key !== " ") return;
													event.preventDefault();
													setCommentsExpanded(open => !open);
												}}
											>
												<span className="prd-comments-mark">
													<ClipboardList size={20} />
												</span>
												<span className="prd-comments-heading">
													<span className="prd-comments-titlerow">
														<span className="prd-comments-title">Plan Review Comments</span>
														<button
															type="button"
															className="prd-comments-open"
															aria-label="Open the plan review comments screen"
															onClick={event => {
																event.stopPropagation();
																navigate(
																	routeNames.PLAN_REVIEW_COMMENTS.replace(":planReviewId", planReviewId).replace(
																		":jobId",
																		jobId
																	)
																);
															}}
														>
															<ArrowUpRight size={13.993} />
														</button>
													</span>
													<span className="prd-comments-chips">
														{COMMENT_COUNT_META.map(([status, label, className]) => {
															const count = flaggedFindings.filter(f => f.status === status).length;
															if (!count) return null;
															return (
																<span key={status} className={`prd-comment-chip ${className}`}>
																	{checkIcon(status, 12)}
																	{count} {label}
																</span>
															);
														})}
														{!hasIssues && <span className="prd-comment-chip">No open items</span>}
													</span>
												</span>
												<ChevronDown
													size={20}
													className={`prd-comments-chevron ${commentsExpanded || isExporting ? "prd-open" : ""}`}
												/>
											</div>

											{(commentsExpanded || isExporting) && (
												<div className="prd-comments-body">
													{listedFlaggedFindings.map(finding => (
														<FindingRow
															key={finding.id}
															finding={finding}
															rowClassName="prd-comment-row"
															modifierPrefix="prd-comment-row"
															label={finding.title ?? ""}
															icon={checkIcon(finding.status ?? "")}
															onSelectFinding={setSelectedFindingId}
														/>
													))}
													{!hasIssues && (
														<p className="prd-comments-empty">This submittal has no findings requiring attention.</p>
													)}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
							)}

							{/* Detailed findings by category */}
							{!isLocked && (
							<div className="prd-section">
								<h2 className="prd-section-title prd-section-title-large">
									<Layers size={20} className="prd-section-icon" />
									Detailed Findings by Category
								</h2>
								<div className="prd-categories">
									{categories.map(([name, findings]) => (
										<ReviewCategory
											key={name}
											name={name}
											findings={findings}
											forceExpanded={isExporting}
											showMarkedIncorrectInPlace={showMarkedIncorrectInPlace}
											onSelectFinding={setSelectedFindingId}
										/>
									))}
									<PlanReviewMarkedIncorrectGroup
										count={markedIncorrectFindings.length}
										isShownInPlace={showMarkedIncorrectInPlace}
										onToggleShownInPlace={() => setShowMarkedIncorrectInPlace(shown => !shown)}
									>
										{markedIncorrectFindings.map(finding => (
											<FindingRow
												key={finding.id}
												finding={finding}
												rowClassName="prd-check-row prd-check-row-clickable"
												modifierPrefix="prd-check-row"
												label={`${checkPrefix(finding.status ?? "")}${finding.title ?? ""}`}
												icon={<span className="prd-check-row-icon">{checkIcon(finding.status ?? "", 20)}</span>}
												onSelectFinding={setSelectedFindingId}
											/>
										))}
									</PlanReviewMarkedIncorrectGroup>
								</div>
							</div>
							)}
						</div>
					</div>
				</div>
			</OllieLayout>

			<PlanReviewFindingModal
				isOpen={!!selectedFinding}
				onClose={() => setSelectedFindingId(null)}
				item={selectedFinding}
				isLatestSubmittal={isLatestSubmittal}
				submissionNumber={submissionNumber}
				jurisdictionId={job?.plan_review?.jurisdiction_id}
				document={selectedFindingDocument}
				onPrevious={() => previousFindingId && setSelectedFindingId(previousFindingId)}
				onNext={() => nextFindingId && setSelectedFindingId(nextFindingId)}
				hasPrevious={!!previousFindingId}
				hasNext={!!nextFindingId}
			/>

			{mustAcknowledgeAhjSubmission && (
				<PlanReviewApprovalAcknowledgmentModal
					jobId={jobId}
					ollieId={job?.plan_review?.ollie_id}
					propertyName={job?.plan_review?.property?.name}
					jurisdictionName={job?.plan_review?.jurisdiction_record?.name}
					jurisdictionWebsiteUrl={job?.plan_review?.jurisdiction_record?.website_url}
				/>
			)}

			<LoadingOverlay isLoading={loading && !job} tip={""} />
		</>
	);
};

export default PlanReviewDetails;
